import { z } from 'zod';
import {
  RecommendationSeverity,
  RecommendationStatus,
  RecommendationType,
  SessionStatus,
  SsoConnectionStatus,
  SsoProtocol,
  SsoProviderType,
} from '../enums/security.enums';
import { ListQuerySchema } from './common.dto';

/**
 * Security DTOs for SSO, Sessions, Key Rotation, and Recommendations
 */

// ============================================================================
// SSO CONFIGURATION
// ============================================================================

/**
 * SSO Configuration Response (from database)
 */
export const SsoConfigSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  type: z.nativeEnum(SsoProtocol),
  providerName: z.string().nullable(),
  isActive: z.boolean(),
  jitProvisioningEnabled: z.boolean(),
  defaultRole: z.string(),
  roleMappingEnabled: z.boolean(),
  enforceEmailVerification: z.boolean(),
  allowPasswordFallback: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SsoConfig = z.infer<typeof SsoConfigSchema>;

/**
 * SSO Status Response (for display)
 */
export const SsoStatusSchema = z.object({
  enabled: z.boolean(),
  provider: z.nativeEnum(SsoProviderType).nullable(),
  providerName: z.string().nullable(),
  protocol: z.nativeEnum(SsoProtocol).nullable(),
  connectionStatus: z.nativeEnum(SsoConnectionStatus),
  lastSyncAt: z.string().datetime().nullable(),
  usersSyncedViaSso: z.number().int().nonnegative(),
  issuerUrl: z.string().nullable(),
  jitProvisioningEnabled: z.boolean(),
  allowPasswordFallback: z.boolean(),
});

export type SsoStatus = z.infer<typeof SsoStatusSchema>;

/**
 * Update SSO Configuration Request
 */
export const UpdateSsoConfigSchema = z.object({
  type: z.nativeEnum(SsoProtocol).optional(),
  providerName: z.string().min(1).max(100).optional(),
  isActive: z.boolean().optional(),

  // SAML Configuration
  samlEntryPoint: z.string().url().optional().nullable(),
  samlIssuer: z.string().optional().nullable(),
  samlCert: z.string().optional().nullable(),
  samlCallbackUrl: z.string().url().optional().nullable(),
  samlLogoutUrl: z.string().url().optional().nullable(),

  // OIDC Configuration
  oidcIssuer: z.string().url().optional().nullable(),
  oidcClientId: z.string().optional().nullable(),
  oidcClientSecret: z.string().optional().nullable(),
  oidcCallbackUrl: z.string().url().optional().nullable(),
  oidcScopes: z.array(z.string()).optional(),

  // Provisioning Settings
  jitProvisioningEnabled: z.boolean().optional(),
  defaultRole: z.string().optional(),
  roleMappingEnabled: z.boolean().optional(),
  roleMapping: z.record(z.string(), z.string()).optional().nullable(),
  attributeMapping: z.record(z.string(), z.string()).optional().nullable(),

  // Security Settings
  enforceEmailVerification: z.boolean().optional(),
  allowPasswordFallback: z.boolean().optional(),
});

export type UpdateSsoConfig = z.infer<typeof UpdateSsoConfigSchema>;

/**
 * Test SSO Connection Response
 */
export const TestSsoConnectionResponseSchema = z.object({
  success: z.boolean(),
  status: z.nativeEnum(SsoConnectionStatus),
  message: z.string(),
  details: z
    .object({
      issuer: z.string().optional(),
      loginUrl: z.string().optional(),
      logoutUrl: z.string().optional(),
      certificateValid: z.boolean().optional(),
      certificateExpiry: z.string().datetime().optional(),
    })
    .optional(),
  error: z.string().optional(),
});

export type TestSsoConnectionResponse = z.infer<typeof TestSsoConnectionResponseSchema>;

// ============================================================================
// DEVICE INFO
// ============================================================================

/**
 * Device Info Schema
 * Parsed user agent data for session tracking
 */
export const DeviceInfoSchema = z.object({
  browser: z.string().optional(),
  browserVersion: z.string().optional(),
  os: z.string().optional(),
  osVersion: z.string().optional(),
  device: z.string().optional(),
  deviceType: z.enum(['desktop', 'mobile', 'tablet', 'unknown']).optional(),
  isBot: z.boolean().optional(),
});

export type DeviceInfo = z.infer<typeof DeviceInfoSchema>;

// ============================================================================
// USER SESSIONS
// ============================================================================

/**
 * User Session (from database)
 */
export const UserSessionSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  businessId: z.string().uuid(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  deviceInfo: DeviceInfoSchema.nullable(),
  location: z.string().nullable(),
  browser: z.string().nullable(),
  os: z.string().nullable(),
  createdAt: z.string().datetime(),
  lastActiveAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
  revokedBy: z.string().uuid().nullable(),
  status: z.nativeEnum(SessionStatus),
  // Enriched user info for display
  userName: z.string().optional(),
  userEmail: z.string().email().optional(),
});

export type UserSession = z.infer<typeof UserSessionSchema>;

/**
 * Session List Query Parameters
 */
export const SessionListQuerySchema = ListQuerySchema.extend({
  userId: z.string().uuid().optional(),
  status: z.nativeEnum(SessionStatus).optional(),
  includeRevoked: z
    .string()
    .optional()
    .transform((val) => val === 'true'),
});

export type SessionListQuery = z.infer<typeof SessionListQuerySchema>;

/**
 * Revoke Session Request
 */
export const RevokeSessionSchema = z.object({
  reason: z.string().max(500).optional(),
});

export type RevokeSession = z.infer<typeof RevokeSessionSchema>;

/**
 * Bulk Revoke Sessions Request
 */
export const BulkRevokeSessionsSchema = z.object({
  sessionIds: z.array(z.string().uuid()).min(1).max(100),
  reason: z.string().max(500).optional(),
});

export type BulkRevokeSessions = z.infer<typeof BulkRevokeSessionsSchema>;

/**
 * Bulk Revoke Response
 */
export const BulkRevokeResponseSchema = z.object({
  revoked: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  errors: z
    .array(
      z.object({
        sessionId: z.string().uuid(),
        message: z.string(),
      }),
    )
    .optional(),
});

export type BulkRevokeResponse = z.infer<typeof BulkRevokeResponseSchema>;

// ============================================================================
// SESSION POLICY
// ============================================================================

/**
 * Session Policy (from database)
 */
export const SessionPolicySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  sessionTimeoutMinutes: z.number().int().positive(),
  idleTimeoutMinutes: z.number().int().positive(),
  maxConcurrentSessions: z.number().int().positive(),
  enforceIpBinding: z.boolean(),
  requireMfaOnNewDevice: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SessionPolicy = z.infer<typeof SessionPolicySchema>;

/**
 * Update Session Policy Request
 *
 * NOTE ON TIMEOUT RANGES VS DEFAULTS
 * ----------------------------------
 * - The *defaults* for these values are defined in the Prisma schema:
 *   - `sessionTimeoutMinutes` default: 480 minutes (8 hours)
 *   - `idleTimeoutMinutes` default: 30 minutes
 * - This DTO only validates the *allowed range* when an administrator updates
 *   the policy. The ranges below are intentionally broader than the defaults:
 *   - `sessionTimeoutMinutes`: 5 minutes to 7 days (10080 minutes)
 *   - `idleTimeoutMinutes`: 5 minutes to 24 hours (1440 minutes)
 *
 * Rationale:
 * - Lower bound (5 minutes) prevents misconfiguration to extremely low values
 *   that would make the system unusable.
 * - Upper bounds (7 days / 24 hours) prevent effectively "never expiring"
 *   sessions or idle timeouts, which would be insecure.
 * - The Prisma defaults (480 and 30) lie safely within these ranges and
 *   represent our recommended baseline configuration.
 *
 * If the business requirements or Prisma defaults change, update this comment
 * and, if necessary, the min/max values below to keep them aligned.
 */
export const UpdateSessionPolicySchema = z.object({
  // Allowed range: 5 minutes to 7 days (10080 minutes). Default is defined in Prisma (480 minutes).
  sessionTimeoutMinutes: z.number().int().min(5).max(10080).optional(),
  // Allowed range: 5 minutes to 24 hours (1440 minutes). Default is defined in Prisma (30 minutes).
  idleTimeoutMinutes: z.number().int().min(5).max(1440).optional(),
  maxConcurrentSessions: z.number().int().min(1).max(100).optional(),
  enforceIpBinding: z.boolean().optional(),
  requireMfaOnNewDevice: z.boolean().optional(),
});

export type UpdateSessionPolicy = z.infer<typeof UpdateSessionPolicySchema>;

// ============================================================================
// KEY ROTATION
// ============================================================================

/**
 * API Key Rotation Status (for a single key)
 */
export const KeyRotationItemSchema = z.object({
  keyId: z.string().uuid(),
  keyName: z.string(),
  keyPrefix: z.string(),
  createdAt: z.string().datetime(),
  lastRotatedAt: z.string().datetime().nullable(),
  ageInDays: z.number().int().nonnegative(),
  isOverdue: z.boolean(),
  daysUntilOverdue: z.number().int().nullable(),
  lastUsedAt: z.string().datetime().nullable(),
  status: z.string(),
});

export type KeyRotationItem = z.infer<typeof KeyRotationItemSchema>;

/**
 * Key Rotation Status Response (all keys for a business)
 */
export const KeyRotationStatusSchema = z.object({
  keys: z.array(KeyRotationItemSchema),
  policy: z.object({
    autoRotationEnabled: z.boolean(),
    rotationIntervalDays: z.number().int().positive(),
    notifyDaysBeforeExpiry: z.number().int().nonnegative(),
    lastRotationCheck: z.string().datetime().nullable(),
  }),
  summary: z.object({
    totalKeys: z.number().int().nonnegative(),
    overdueKeys: z.number().int().nonnegative(),
    keysNeedingAttention: z.number().int().nonnegative(),
    averageKeyAgeDays: z.number().nonnegative(),
  }),
});

export type KeyRotationStatus = z.infer<typeof KeyRotationStatusSchema>;

/**
 * Rotate API Key Request
 */
export const RotateApiKeyRequestSchema = z.object({
  keyId: z.string().uuid(),
  reason: z.string().max(500).optional(),
});

export type RotateApiKeyRequest = z.infer<typeof RotateApiKeyRequestSchema>;

/**
 * Rotate API Key Response
 */
export const RotateApiKeyResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  prefix: z.string(),
  key: z.string(), // The new plain-text key (shown only once)
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime().nullable(),
  rotatedFromKeyId: z.string().uuid(),
});

export type RotateApiKeyResponse = z.infer<typeof RotateApiKeyResponseSchema>;

/**
 * API Key Rotation History Entry
 */
export const ApiKeyRotationHistorySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  keyId: z.string().uuid(),
  keyName: z.string(),
  rotatedAt: z.string().datetime(),
  rotatedBy: z.string().uuid(),
  rotatedByName: z.string().optional(),
  rotatedByEmail: z.string().email().optional(),
  reason: z.string().nullable(),
  oldKeyPrefix: z.string().nullable(),
  newKeyPrefix: z.string().nullable(),
});

export type ApiKeyRotationHistory = z.infer<typeof ApiKeyRotationHistorySchema>;

/**
 * Key Rotation History Query Parameters
 */
export const KeyRotationHistoryQuerySchema = ListQuerySchema.extend({
  keyId: z.string().uuid().optional(),
});

export type KeyRotationHistoryQuery = z.infer<typeof KeyRotationHistoryQuerySchema>;

// ============================================================================
// KEY ROTATION POLICY
// ============================================================================

/**
 * Key Rotation Policy (from database)
 */
export const KeyRotationPolicySchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  autoRotationEnabled: z.boolean(),
  rotationIntervalDays: z.number().int().positive(),
  notifyDaysBeforeExpiry: z.number().int().nonnegative(),
  lastRotationCheck: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type KeyRotationPolicy = z.infer<typeof KeyRotationPolicySchema>;

/**
 * Update Key Rotation Policy Request
 */
export const UpdateKeyRotationPolicySchema = z.object({
  autoRotationEnabled: z.boolean().optional(),
  rotationIntervalDays: z.number().int().min(7).max(365).optional(), // 1 week to 1 year
  notifyDaysBeforeExpiry: z.number().int().min(0).max(30).optional(), // 0 to 30 days
});

export type UpdateKeyRotationPolicy = z.infer<typeof UpdateKeyRotationPolicySchema>;

// ============================================================================
// SECURITY RECOMMENDATIONS
// ============================================================================

/**
 * Security Recommendation (from database)
 */
export const SecurityRecommendationSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  type: z.nativeEnum(RecommendationType),
  severity: z.nativeEnum(RecommendationSeverity),
  title: z.string(),
  description: z.string(),
  remediation: z.string(),
  status: z.nativeEnum(RecommendationStatus),
  dismissedBy: z.string().uuid().nullable(),
  dismissedAt: z.string().datetime().nullable(),
  resolvedBy: z.string().uuid().nullable(),
  resolvedAt: z.string().datetime().nullable(),
  metadata: z.record(z.string(), z.any()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SecurityRecommendation = z.infer<typeof SecurityRecommendationSchema>;

/**
 * Recommendation List Query Parameters
 */
export const RecommendationListQuerySchema = ListQuerySchema.extend({
  severity: z.nativeEnum(RecommendationSeverity).optional(),
  status: z.nativeEnum(RecommendationStatus).optional(),
  type: z.nativeEnum(RecommendationType).optional(),
});

export type RecommendationListQuery = z.infer<typeof RecommendationListQuerySchema>;

/**
 * Dismiss Recommendation Request
 */
export const DismissRecommendationSchema = z.object({
  reason: z.string().max(500).optional(),
});

export type DismissRecommendation = z.infer<typeof DismissRecommendationSchema>;

/**
 * Resolve Recommendation Request
 */
export const ResolveRecommendationSchema = z.object({
  notes: z.string().max(1000).optional(),
});

export type ResolveRecommendation = z.infer<typeof ResolveRecommendationSchema>;

// ============================================================================
// SECURITY SCORE
// ============================================================================

/**
 * Security Score Response
 */
export const SecurityScoreSchema = z.object({
  score: z.number().min(0).max(100),
  grade: z.enum(['A', 'B', 'C', 'D', 'F']),
  breakdown: z.object({
    ssoEnabled: z.number().min(0).max(20),
    mfaAdoption: z.number().min(0).max(25),
    sessionSecurity: z.number().min(0).max(20),
    keyRotation: z.number().min(0).max(15),
    recommendations: z.number().min(0).max(20),
  }),
  factors: z.array(
    z.object({
      name: z.string(),
      status: z.enum(['good', 'warning', 'critical']),
      impact: z.number(),
      description: z.string(),
    }),
  ),
  lastCalculatedAt: z.string().datetime(),
});

export type SecurityScore = z.infer<typeof SecurityScoreSchema>;

// ============================================================================
// SECURITY OVERVIEW (COMBINED STATS)
// ============================================================================

/**
 * Security Overview for Dashboard
 */
export const SecurityOverviewSchema = z.object({
  score: SecurityScoreSchema,
  ssoStatus: SsoStatusSchema,
  sessionStats: z.object({
    activeSessions: z.number().int().nonnegative(),
    sessionsLast24h: z.number().int().nonnegative(),
    averageSessionDuration: z.number().nonnegative(), // in minutes
  }),
  keyRotationStats: z.object({
    totalKeys: z.number().int().nonnegative(),
    overdueKeys: z.number().int().nonnegative(),
    daysSinceLastRotation: z.number().int().nullable(),
  }),
  recommendationStats: z.object({
    total: z.number().int().nonnegative(),
    critical: z.number().int().nonnegative(),
    high: z.number().int().nonnegative(),
    medium: z.number().int().nonnegative(),
    low: z.number().int().nonnegative(),
  }),
});

export type SecurityOverview = z.infer<typeof SecurityOverviewSchema>;
